import AutoStoriesIcon from "@mui/icons-material/AutoStories";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import {
  Box,
  Chip,
  List,
  ListItem,
  ListItemButton,
  Sheet,
  Typography,
  useColorScheme,
} from "@mui/joy";
import { hatchlingPoems } from "../../data/hatchlingData";
import { Chapter } from "../../types/novellaTypes";
import {
  getReadPoems,
  hatchlingChapterToPoemMapping,
  isChapterCompleted,
  resetReadingProgress,
} from "../../utils/cookieUtils";

interface HatchlingChapterNavigationProps {
  chapters: Chapter[];
  currentChapter: Chapter;
  onChapterChange: (chapter: Chapter) => void;
}

const HatchlingChapterNavigation = ({
  chapters,
  currentChapter,
  onChapterChange,
}: HatchlingChapterNavigationProps) => {
  const { mode } = useColorScheme();
  const isDark = mode === "dark";

  // Ember color for Hatchling
  const emberColor = isDark
    ? "rgba(239, 68, 68, 0.9)"
    : "rgba(185, 28, 28, 0.9)";

  return (
    <Sheet
      variant="outlined"
      sx={{
        borderRadius: "lg",
        overflow: "hidden",
        border: "1px solid",
        borderColor: isDark ? "neutral.800" : "neutral.200",
        position: "sticky",
        top: 20,
        maxHeight: "calc(100vh - 40px)",
        overflowY: "auto",
      }}
    >
      <Box
        sx={{
          p: 2,
          borderBottom: "1px solid",
          borderColor: "divider",
          bgcolor: isDark ? "neutral.900" : "neutral.50",
        }}
      >
        <Typography
          level="title-md"
          sx={{
            fontFamily: "'Cinzel', serif",
            textAlign: "center",
            color: emberColor,
          }}
        >
          Chapters
        </Typography>
      </Box>

      <List size="sm" sx={{ p: 1 }}>
        {chapters
          .filter((chapter) => chapter.id !== "poems")
          .map((chapter) => {
            const isActive = currentChapter.id === chapter.id;
            const isCompleted = isChapterCompleted(chapter.id);

            return (
              <ListItem key={chapter.id}>
                <ListItemButton
                  selected={isActive}
                  onClick={() => onChapterChange(chapter)}
                  sx={{
                    borderRadius: "md",
                    py: 1.5,
                    px: 2,
                    "&.Mui-selected": {
                      bgcolor: isDark
                        ? "rgba(239, 68, 68, 0.15)"
                        : "rgba(239, 68, 68, 0.1)",
                      "&:hover": {
                        bgcolor: isDark
                          ? "rgba(239, 68, 68, 0.2)"
                          : "rgba(239, 68, 68, 0.15)",
                      },
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      width: "100%",
                    }}
                  >
                    <Typography
                      level="body-sm"
                      sx={{
                        fontFamily: "'Cinzel', serif",
                        fontWeight: isActive ? "bold" : "normal",
                        color: isActive
                          ? emberColor
                          : isDark
                          ? "neutral.300"
                          : "neutral.700",
                      }}
                    >
                      {chapter.title}
                    </Typography>
                    {isCompleted && (
                      <CheckCircleIcon
                        sx={{
                          fontSize: "1rem",
                          color: "success.500",
                          ml: 1,
                        }}
                      />
                    )}
                  </Box>
                </ListItemButton>
              </ListItem>
            );
          })}
      </List>

      {/* Reset Progress Button */}
      <Box sx={{ mt: 2, mb: 1, textAlign: "center" }}>
        <Box
          onClick={resetReadingProgress}
          sx={{
            py: 1.5,
            px: 2,
            mx: "auto",
            display: "inline-block",
            borderRadius: "md",
            cursor: "pointer",
            fontFamily: "'Cinzel', serif",
            fontSize: "0.85rem",
            fontWeight: "medium",
            border: "1px solid",
            borderColor: isDark ? "neutral.700" : "neutral.300",
            color: isDark ? "neutral.400" : "neutral.600",
            bgcolor: isDark ? "neutral.900" : "background.body",
            transition: "all 0.2s",
            "&:hover": {
              bgcolor: isDark ? "error.900" : "error.100",
              color: isDark ? "error.200" : "error.700",
              borderColor: isDark ? "error.700" : "error.300",
            },
          }}
        >
          Reset Reading Progress
        </Box>
      </Box>

      {/* Poems Section - Only visible when poems are unlocked */}
      {getReadPoems().length > 0 && (
        <Box sx={{ mt: 2, borderTop: "1px solid", borderColor: "divider" }}>
          <Box
            sx={{
              p: 2,
              borderBottom: "1px solid",
              borderColor: "divider",
              bgcolor: isDark ? "neutral.900" : "neutral.50",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
            }}
          >
            <Typography
              level="title-md"
              sx={{
                fontFamily: "'Cinzel', serif",
                color: emberColor,
              }}
            >
              Poems
            </Typography>
            <Chip
              size="sm"
              variant="outlined"
              sx={{
                fontFamily: "'EB Garamond', serif",
                cursor: "pointer",
                borderColor: emberColor,
                color: emberColor,
                "&:hover": {
                  bgcolor: isDark
                    ? "rgba(239, 68, 68, 0.15)"
                    : "rgba(239, 68, 68, 0.1)",
                },
              }}
              startDecorator={
                <AutoStoriesIcon fontSize="small" sx={{ color: emberColor }} />
              }
              onClick={() => {
                const poemsChapter = chapters.find(
                  (chapter) => chapter.id === "poems"
                );
                if (poemsChapter) {
                  if (window.location.hash) {
                    window.history.pushState(
                      "",
                      document.title,
                      window.location.pathname
                    );
                  }
                  onChapterChange(poemsChapter);
                }
              }}
            >
              {getReadPoems().length}
            </Chip>
          </Box>
          <List size="sm" sx={{ p: 1 }}>
            {Object.entries(hatchlingChapterToPoemMapping)
              .filter(([, poemId]) => getReadPoems().includes(poemId))
              .map(([, poemId]) => {
                const poemTitle = hatchlingPoems[poemId]?.title;

                if (!poemTitle) return null;

                const isSelected =
                  currentChapter.id === "poems" &&
                  window.location.hash === `#${poemId}`;

                return (
                  <ListItem key={poemId}>
                    <ListItemButton
                      selected={isSelected}
                      onClick={() => {
                        const poemsChapter = chapters.find(
                          (chapter) => chapter.id === "poems"
                        );
                        if (poemsChapter) {
                          window.location.hash = poemId;
                          onChapterChange(poemsChapter);
                        }
                      }}
                      sx={{
                        borderRadius: "md",
                        py: 1.5,
                        px: 2,
                        bgcolor: isSelected
                          ? isDark
                            ? "rgba(239, 68, 68, 0.15)"
                            : "rgba(239, 68, 68, 0.1)"
                          : "transparent",
                      }}
                    >
                      <Typography
                        level="body-sm"
                        sx={{
                          fontFamily: "'Cinzel', serif",
                          fontWeight: isSelected ? "bold" : "normal",
                          color: emberColor,
                        }}
                      >
                        {poemTitle}
                      </Typography>
                    </ListItemButton>
                  </ListItem>
                );
              })}
          </List>
        </Box>
      )}
    </Sheet>
  );
};

export default HatchlingChapterNavigation;
